import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { BasicEventsController } from "../../../touch_events_controller/basic_events_controller.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { DAILY_CHALLENGE_REWARD_ICONS } from "../../../daily_challenge/daily_challenge_rewards.js";
import { DailyChallengeService } from "../../../daily_challenge/daily_challenge_service.js";
import { NetworkManager } from "../../../network_manager.js";
import { formatShortDuration } from "../../../common/common_funcs.js";
import { rewardAmountText } from "../../../common/reward_format.js";
import { RewardTooltip } from "../../tooltip/reward_tooltip.js";
import { OverlayFactory } from "../../overlay/overlay_factory.js";
import { OverlayId } from "../../overlay/overlay_ids.js";
import { ScreenManager, Screens } from "../../../screen_manager/screen_manager.js";
import { AudioClips } from "../../../audio/audio_constants.js";
import { audioController } from "../../../audio/audio_controller.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";
import { DAILY_CHALLENGE_DAYS_COUNT } from "../../../daily_challenge/daily_challenge_config.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { Game } from "../../../game.js";
import { SafeAreas } from "../../safe_areas.js";
import { DialogManager } from "../../dialog/dialog_manager.js";
import { PlayDialogState } from "../../dialog/play_dialog.js";
import { DailyChallengePlayDialog } from "../../dialog/daily_challenge_play_dialog.js";

enum DayState
{
  Completed,
  Current,
  Locked,
}

function imgIdForDayState( state: DayState ): ImgID
{
  switch ( state )
  {
    case DayState.Completed: return ImgID.UI_DailyChallengeCompletedLvl;
    case DayState.Current:   return ImgID.UI_DailyChallengeCurrentLvl;
    case DayState.Locked:    return ImgID.UI_DailyChallengeNextLvl;
  }
}

function stateForDay( day: number, completed_days: number ): DayState
{
  if ( day <= completed_days )      return DayState.Completed;
  if ( day === completed_days + 1 ) return DayState.Current;
  return DayState.Locked;
}

interface DayCardRefs
{
  bg: UIElement;
  is_last: boolean;
  play_btn: UINineSliceElement;
  completed_icon: UIImage;
  locked_icon: UIImage;
  reward_icons: UIImage[];
}

export class DailyChallengeScreen extends UIElement
{
  private events_controller: BasicEventsController | null = null;
  private background_gradient: CanvasGradient | null = null;
  private timer_text: UITextElement | null = null;
  private timer_refreshed_at = 0;
  private last_timer_text = "";
  private rollover_attempt_at = 0;
  private stage_loading = false;
  private reuse_key = "";

  private readonly BACKGROUND_IMAGE_W = 1440;
  private readonly BACKGROUND_IMAGE_H = 760;
  private readonly BACKGROUND_EXTRA_BELOW_BORDER_Y = 100;

  private readonly BORDER_W = 1074;
  private readonly BORDER_H = 1159;
  private readonly BORDER_BOTTOM_MARGIN = 80;

  private readonly ICON_INFO_Y = 50;
  private readonly ICON_CLOSE_Y = 72;
  private readonly ICON_INFO_X = 15;
  private readonly ICON_CLOSE_X = 25;

  private readonly TITLE_Y = 50;
  private readonly TITLE_H = 150;
  private readonly TITLE_SIDE_CLEARANCE = 175;

  private readonly TIMER_TEXT_X = 130;
  private readonly TIMER_TEXT_W = 175;
  private readonly TIMER_TEXT_Y = 48;
  private readonly TIMER_TEXT_H = 62;

  private readonly DAY_ITEM_W = 404;
  private readonly DAY_ITEM_H = 264;
  private readonly LAST_DAY_ITEM_W = 862;
  private readonly DAY_ITEM_START_Y = 80;

  private readonly DAY_ARROW_W = 62;
  private readonly DAY_ARROW_H = 164;

  private static readonly ANIM_INITIAL_DELAY_MS = 300;
  private static readonly ANIM_POP_MS = 280;
  private static readonly ANIM_ARROW_MS = 220;
  private static readonly ANIM_STAGE_GAP_MS = 120;

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number )
  {
    super( ctx, x, y, width, height, false, false );

    this.initializeEventsController( this.ctx.canvas );

    this.createBackground();
    this.createHeader();
    this.createContent();
    this.reuse_key = DailyChallengeScreen.computeReuseKey();
  }

  private static computeReuseKey(): string
  {
    const stats = StatisticsStorage.load();
    const cached = DailyChallengeService.getCached();
    return JSON.stringify( [
      stats.daily_challenge_completed_days,
      stats.daily_challenge_seen_completed_days,
      cached ? { ...cached, seconds_until_reset: undefined } : null,
    ] );
  }

  public canReuseScreen(): boolean
  {
    return !this.stage_loading && this.reuse_key === DailyChallengeScreen.computeReuseKey();
  }

  public onScreenHidden(): void
  {
    this.events_controller?.disable();
    this.reuse_key = DailyChallengeScreen.computeReuseKey();
  }

  public onScreenShown(): void
  {
    this.events_controller?.enable();
    this.refreshTimerText();
  }

  private createHeader(): void
  {
    const y = LayoutScaler.yFromDesign( this.TITLE_Y );
    const h = LayoutScaler.heightFromDesign( this.TITLE_H );
    // Keep the title clear of the info/close buttons. Long localizations then
    // wrap naturally into two lines instead of continuing underneath them.
    const title_x = LayoutScaler.xFromDesign( this.TITLE_SIDE_CLEARANCE );
    const title_w = this.rect.getW() - title_x * 2;

    const title_1 = new UITextElement( this.ctx, title_x, y + LayoutScaler.yFromDesign( 6 ), title_w, h, {
      localization_key: "DAILY.CHALLENGE",
      font_size: LayoutScaler.textSizeFromDesign( 70 ),
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_style: TextStyleScheme.geologica,
      text_color: ColorScheme.white,
      font_weight: 900,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 32 ),
      outline_color: ColorScheme.daily_challenge_title_outline_3,
      auto_wrap: true,
      fit_font_size: true,
      min_font_size: LayoutScaler.textSizeFromDesign( 38 ),
      line_height: 0.95,
    } );

    const title_2 = new UITextElement( this.ctx, title_x, y + LayoutScaler.yFromDesign( 3 ), title_w, h, {
      localization_key: "DAILY.CHALLENGE",
      font_size: LayoutScaler.textSizeFromDesign( 70 ),
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_style: TextStyleScheme.geologica,
      text_color: ColorScheme.white,
      font_weight: 900,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 22 ),
      outline_color: ColorScheme.daily_challenge_title_outline,
      shadow_blur: LayoutScaler.widthFromDesign( 6 ),
      shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      shadow_color: ColorScheme.black_transparent,
      auto_wrap: true,
      fit_font_size: true,
      min_font_size: LayoutScaler.textSizeFromDesign( 38 ),
      line_height: 0.95,
    } );

    const title_3 = new UITextElement( this.ctx, title_x, y + LayoutScaler.yFromDesign( 3 ), title_w, h, {
      localization_key: "DAILY.CHALLENGE",
      font_size: LayoutScaler.textSizeFromDesign( 70 ),
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_style: TextStyleScheme.geologica,
      text_color: ColorScheme.white,
      font_weight: 900,
      outline_color: ColorScheme.daily_challenge_title_outline_2,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
      auto_wrap: true,
      fit_font_size: true,
      min_font_size: LayoutScaler.textSizeFromDesign( 38 ),
      line_height: 0.95,
    } );

    const title_4 = new UITextElement( this.ctx, title_x, y, title_w, h, {
      localization_key: "DAILY.CHALLENGE",
      font_size: LayoutScaler.textSizeFromDesign( 70 ),
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_style: TextStyleScheme.geologica,
      text_color: ColorScheme.white,
      font_weight: 900,
      auto_wrap: true,
      fit_font_size: true,
      min_font_size: LayoutScaler.textSizeFromDesign( 38 ),
      line_height: 0.95,
    } );

    this.addChild( title_1 );
    this.addChild( title_2 );
    this.addChild( title_3 );
    this.addChild( title_4 );

    const info_icon_w = LayoutScaler.widthFromDesign( 150 );
    const info_icon_h = LayoutScaler.heightFromDesign( 150 );
    const info_icon_x = this.ICON_INFO_X;
    const info_icon_y = LayoutScaler.yFromDesign( this.ICON_INFO_Y );

    const info_icon = new UIImage( this.ctx, info_icon_x, info_icon_y, info_icon_w, info_icon_h, ImgID.UI_LeaderboardInfoButton, {}, true );
    this.addChild( info_icon ); 

    info_icon.setOnClick( () => OverlayFactory.openOverlay( OverlayId.DAILY_CHALLENGE_INFO_OVERLAY ) );

    const close_icon_w = LayoutScaler.widthFromDesign( 110 );
    const close_icon_h = LayoutScaler.heightFromDesign( 110 );
    const close_icon_x = this.rect.getW() - close_icon_w - this.ICON_CLOSE_X;
    const close_icon_y = LayoutScaler.yFromDesign( this.ICON_CLOSE_Y );

    const close_icon = new UIImage( this.ctx, close_icon_x, close_icon_y, close_icon_w, close_icon_h, ImgID.UI_SettingsDialogCloseBtn, {}, true );
    this.addChild( close_icon );

    close_icon.setOnClick( () => ScreenManager.showScreen( Screens.MENU ) );
  }

  // Shared with createContent() so the border is only positioned once, in
  // one place, and the background can be sized against that same Y.
  private computeBorderY(): number
  {
    const border_h = LayoutScaler.heightFromDesign( this.BORDER_H );
    return this.rect.getH() - border_h - LayoutScaler.yFromDesign( this.BORDER_BOTTOM_MARGIN ) + Game.safeAreaBottom();
  }

  private createBackground(): void
  {
    const bg_bottom = this.computeBorderY()
      + LayoutScaler.heightFromDesign( this.BACKGROUND_EXTRA_BELOW_BORDER_Y );
    // The screen root starts below the top safe area. Let decorative artwork
    // extend behind it to the physical top, while preserving its old bottom
    // edge and aspect ratio. Interactive header elements remain in the safe
    // content coordinates.
    const bg_y = -Game.safeAreaTop();
    const bg_h = bg_bottom - bg_y;
    const bg_w = LayoutScaler.widthFromDesign( this.BACKGROUND_IMAGE_W ) * bg_h / LayoutScaler.heightFromDesign( this.BACKGROUND_IMAGE_H );
    const bg_x = ( this.rect.getW() - bg_w ) / 2;

    const bg = new UIImage( this.ctx, bg_x, bg_y, bg_w, bg_h, ImgID.UI_DailyChallengeBg );
    this.addChild( bg );
  }

  private createContent(): void
  {
    const border_w = LayoutScaler.widthFromDesign( this.BORDER_W );
    const border_h = LayoutScaler.heightFromDesign( this.BORDER_H );
    const border_x = ( this.rect.getW() - border_w ) / 2;
    const border_y = this.computeBorderY();

    const border = new UINineSliceElement( this.ctx, border_x, border_y, border_w, border_h, ImgID.UI_DailyChallengeContentBorder, "daily_challenge_content_border" );
    this.addChild( border );

    const content_bg_w = LayoutScaler.widthFromDesign( 932 );
    const content_bg_h = LayoutScaler.heightFromDesign( 1030 );
    const content_bg_x = ( border_w - content_bg_w ) / 2;
    const content_bg_y = LayoutScaler.yFromDesign( 72 );

    const content_bg = new UINineSliceElement( this.ctx, content_bg_x, content_bg_y, content_bg_w, content_bg_h, ImgID.UI_DailyChallengeContentBg, "daily_challenge_content_bg" );
    border.addChild( content_bg );

    const content_title_h = LayoutScaler.heightFromDesign( 62 );
    const content_title_y = LayoutScaler.yFromDesign( 12 );

    const content_title = new UITextElement( this.ctx, 0, content_title_y, content_bg_w, content_title_h, {
      localization_key: "GRAND_PRIZE",
      font_size: LayoutScaler.textSizeFromDesign( 45 ),
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_style: TextStyleScheme.geologica,
      text_color: ColorScheme.daily_challenge_content_title,
      font_weight: 900,
    } );

    const content_title_1 = new UITextElement( this.ctx, 0, content_title_y, content_bg_w, content_title_h, {
      localization_key: "GRAND_PRIZE",
      font_size: LayoutScaler.textSizeFromDesign( 45 ),
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_style: TextStyleScheme.geologica,
      text_color: ColorScheme.daily_challenge_content_title,
      font_weight: 900,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 16 ),
      outline_color: ColorScheme.daily_challenge_content_title_outline
    } );

    content_bg.addChild( content_title );
    content_bg.addChild( content_title_1 );

    const timer_bg_w = LayoutScaler.widthFromDesign( 378 );
    const timer_bg_h = LayoutScaler.heightFromDesign( 130 );
    const timer_bg_x = ( border_w - timer_bg_w ) / 2;
    const timer_bg_y = LayoutScaler.yFromDesign( -68 );

    const timer_bg = new UIImage( this.ctx, timer_bg_x, timer_bg_y, timer_bg_w, timer_bg_h, ImgID.UI_DailyChallengeTimerBg );
    border.addChild( timer_bg );

    const text_x = LayoutScaler.widthFromDesign( this.TIMER_TEXT_X );
    const text_w = LayoutScaler.widthFromDesign( this.TIMER_TEXT_W );

    const text_h = LayoutScaler.heightFromDesign( this.TIMER_TEXT_H );
    const text_y = LayoutScaler.yFromDesign( this.TIMER_TEXT_Y );

    this.last_timer_text = DailyChallengeScreen.resetCountdownText();
    const text = new UITextElement( this.ctx, text_x, text_y, text_w, text_h, {
      text: this.last_timer_text,
      font_size: LayoutScaler.textSizeFromDesign( 45 ),
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_style: TextStyleScheme.geologica,
      text_color: ColorScheme.white,
      font_weight: 800,
      auto_wrap: false,
      fit_font_size: true
    } );

    timer_bg.addChild( text );
    this.timer_text = text;

    // The state is normally prefetched by the menu widget; this covers a
    // direct entry so the countdown and rewards still turn live.
    void DailyChallengeService.fetch().then( () => this.refreshTimerText() );

    this.createList( content_bg );
  }

  private createList( parent: UINineSliceElement ): void
  {
    const stats = StatisticsStorage.load();
    const target_days = stats.daily_challenge_completed_days;

    const seen_days = Math.min( stats.daily_challenge_seen_completed_days, target_days );

    const content_w = parent.getRect().getW();

    const last_item_w = LayoutScaler.widthFromDesign( this.LAST_DAY_ITEM_W );
    const item_w = LayoutScaler.widthFromDesign( this.DAY_ITEM_W );
    const item_h = LayoutScaler.heightFromDesign( this.DAY_ITEM_H );

    const side_pad = ( content_w - last_item_w ) / 2;
    const item_gap_x = last_item_w - item_w * 2;

    const left_x = side_pad;
    const right_x = side_pad + item_w + item_gap_x;

    const item_gap_y = item_gap_x;

    const arrow_h_w = LayoutScaler.widthFromDesign( this.DAY_ARROW_W );
    const arrow_h_h = LayoutScaler.heightFromDesign( this.DAY_ARROW_H );
    const arrow_v_w = arrow_h_h;
    const arrow_v_h = arrow_h_w;

    const top_y = LayoutScaler.yFromDesign( this.DAY_ITEM_START_Y );
    const middle_y = top_y + item_h + item_gap_y;
    const bottom_y = middle_y + item_h + item_gap_y;

    const day_refs = new Map<number, DayCardRefs>();

    const addItem = ( x: number, y: number, day_txt: number, is_last: boolean = false ): void =>
    {
      let bg: UIElement;

      const state = stateForDay( day_txt, seen_days );
      const is_completed = state === DayState.Completed;
      const is_current = state === DayState.Current;
      const is_locked = state === DayState.Locked;

      const img_id = imgIdForDayState( state );

      if ( is_last )
      {
        bg = new UINineSliceElement( this.ctx, x, y, last_item_w, item_h, img_id, "daily_challenge_last_day" );
      } else {
        bg = new UIImage( this.ctx, x, y, item_w, item_h, img_id );
      }

      parent.addChild( bg );
      
      const day_w = LayoutScaler.widthFromDesign( 30 );
      const day_h = LayoutScaler.heightFromDesign( 58 );

      const day_x = LayoutScaler.xFromDesign( 46 );
      const day_y = LayoutScaler.yFromDesign( 32 );

      const day = new UITextElement( this.ctx, day_x, day_y, day_w, day_h, {
        text: day_txt.toString(),
        font_size: LayoutScaler.textSizeFromDesign( 42 ),
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        font_weight: 800,
        outline_color: ColorScheme.daily_challenge_day_txt,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      } );

      const day_1 = new UITextElement( this.ctx, day_x, day_y, day_w, day_h, {
        text: day_txt.toString(),
        font_size: LayoutScaler.textSizeFromDesign( 42 ),
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        font_weight: 800,
      } );

      bg.addChild( day );
      bg.addChild( day_1 );

      const completed_icon_w = LayoutScaler.widthFromDesign( 99 );
      const completed_icon_h = LayoutScaler.heightFromDesign( 91 );
      const completed_icon_x = ( bg.getRect().getW() - completed_icon_w ) / 2;
      const completed_icon_y = bg.getRect().getH() - completed_icon_h - LayoutScaler.yFromDesign( 15 );

      const completed_icon = new UIImage( this.ctx, completed_icon_x, completed_icon_y, completed_icon_w, completed_icon_h, ImgID.IconDailyChallengeCompleted );
      bg.addChild( completed_icon );
      completed_icon.setOpacityImmediate( is_completed ? 1 : 0 );

      const locked_w = LayoutScaler.widthFromDesign( 78 );
      const locked_h = LayoutScaler.heightFromDesign( 93 );
      const locked_x = ( bg.getRect().getW() - locked_w ) / 2;
      const locked_y = bg.getRect().getH() - locked_h - LayoutScaler.yFromDesign( 12 );

      const locked = new UIImage( this.ctx, locked_x, locked_y, locked_w, locked_h, ImgID.IconDailyChallengeLocked );
      bg.addChild( locked );
      locked.setOpacityImmediate( is_locked ? 1 : 0 );

      const play_btn_w = bg.getRect().getW() + LayoutScaler.xFromDesign( 10 );
      const play_btn_h = LayoutScaler.heightFromDesign( 147 );
      const play_btn_x = ( bg.getRect().getW() - play_btn_w ) / 2;
      const play_btn_y = bg.getRect().getH() - play_btn_h + LayoutScaler.yFromDesign( 7 );

      const play_btn = new UINineSliceElement( this.ctx, play_btn_x, play_btn_y, play_btn_w, play_btn_h, ImgID.UI_DailyChallengePlayButton, "daily_challenge_play_button", undefined, {}, true );
      
      const play_text = new UITextElement( this.ctx, 0, 0, play_btn_w, play_btn_h, {
        localization_key: "PLAY",
        font_size: LayoutScaler.textSizeFromDesign( 37 ),
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        font_weight: 800,
        outline_color: ColorScheme.more_lives_buy_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_offset_y: LayoutScaler.yFromDesign( 2 ),
        shadow_blur: LayoutScaler.widthFromDesign( 6 ),
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
        shadow_color: ColorScheme.black_transparent
      } );

      play_btn.addChild( play_text, true );

      play_btn.setOnClick( () =>
      {
        void audioController.playSound( AudioClips.UI_CLICK );
        void this.startStage( day_txt );
      } );

      bg.addChild( play_btn );
      play_btn.setOpacityImmediate( is_current ? 1 : 0 );
      play_btn.setClickable( is_current );

      const bg_w = is_last ? last_item_w : item_w;
      const rewards = DailyChallengeService.getRewards( day_txt );

      const icon_w = LayoutScaler.widthFromDesign( 100 );
      const icon_h = LayoutScaler.heightFromDesign( 100 );
      const icons_y = LayoutScaler.yFromDesign( 28 );
      const icons_start_x = ( bg_w - rewards.length * icon_w ) / 2;

      const reward_icons: UIImage[] = [];

      rewards.forEach( ( reward, i ) =>
      {
        const icon = new UIImage( this.ctx, icons_start_x + i * icon_w, icons_y, icon_w, icon_h, DAILY_CHALLENGE_REWARD_ICONS[ reward.type ] );
        RewardTooltip.attachLegacy( icon, reward.type );
        bg.addChild( icon );
        reward_icons.push( icon );

        const count_w = LayoutScaler.widthFromDesign( 150 );
        const count_h = LayoutScaler.heightFromDesign( 52 );
        const count_x = ( icon_w - count_w ) / 2;
        const count_y = icon_h - count_h + LayoutScaler.yFromDesign( 15 );
        const count_outline_y = count_y + LayoutScaler.yFromDesign( 2 );

        const count_style = {
          text: rewardAmountText( reward.type, reward.amount ),
          font_size: LayoutScaler.textSizeFromDesign( 38 ),
          text_align: MyTextAlign.Center,
          text_style: TextStyleScheme.geologica,
          text_color: ColorScheme.white,
          font_weight: 900,
          auto_wrap: false,
          scale_to_width: true,
        };

        const count = new UITextElement( this.ctx, count_x, count_outline_y, count_w, count_h, {
          ...count_style,
          outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
          outline_color: ColorScheme.booster_dialog_quantity_outline,
        } );

        const count_1 = new UITextElement( this.ctx, count_x, count_y, count_w, count_h, count_style );

        // The amount sits over the icon; let taps through to its tooltip.
        count.setClickable( false );
        count_1.setClickable( false );
        icon.addChild( count );
        icon.addChild( count_1 );

        if ( is_completed )
          icon.setOpacityImmediate( 0.5 );
      } );

      day_refs.set( day_txt, { bg, is_last, play_btn, completed_icon, locked_icon: locked, reward_icons } );
    };

    const addHorizontalArrow = ( row_y: number, img_id: ImgID ): UIImage =>
    {
      const x = left_x + item_w + item_gap_x / 2 - arrow_h_w / 2;
      const y = row_y + item_h / 2 - arrow_h_h / 2;
      const arrow = new UIImage( this.ctx, x, y, arrow_h_w, arrow_h_h, img_id );
      parent.addChild( arrow );
      return arrow;
    };

    const addVerticalArrow = ( item_x: number, row_y: number ): UIImage =>
    {
      const x = item_x + item_w / 2 - arrow_v_w / 2;
      const y = row_y - item_gap_y / 2 - arrow_v_h / 2;
      const arrow = new UIImage( this.ctx, x, y, arrow_v_w, arrow_v_h, ImgID.UI_DailyChallengeTopArrow );
      parent.addChild( arrow );
      return arrow;
    };

    addItem( side_pad, top_y, DAILY_CHALLENGE_DAYS_COUNT, true );

    addItem( left_x, middle_y, 4 );
    addItem( right_x, middle_y, 3 );

    addItem( left_x, bottom_y, 1 );
    addItem( right_x, bottom_y, 2 );

    const arrows = new Map<number, UIImage>( [
      [ 1, addHorizontalArrow( bottom_y, ImgID.UI_DailyChallengeRightArrow ) ],
      [ 2, addVerticalArrow( right_x, bottom_y ) ],
      [ 3, addHorizontalArrow( middle_y, ImgID.UI_DailyChallengeLeftArrow ) ],
      [ 4, addVerticalArrow( left_x, middle_y ) ],
    ] );

    if ( seen_days < target_days )
      this.playCatchUpAnimation( day_refs, arrows, seen_days, target_days );

    if ( stats.daily_challenge_seen_completed_days !== target_days )
      StatisticsStorage.update( { daily_challenge_seen_completed_days: target_days } );
  }

  private playCatchUpAnimation(
    day_refs: Map<number, DayCardRefs>,
    arrows: Map<number, UIImage>,
    from_days: number,
    target_days: number
  ): void
  {
    let t = performance.now() + DailyChallengeScreen.ANIM_INITIAL_DELAY_MS;

    for ( let day = from_days + 1; day <= target_days; day++ )
    {
      const refs = day_refs.get( day );
      if ( refs )
        this.transitionCardTo( refs, DayState.Completed, t );
      t += DailyChallengeScreen.ANIM_POP_MS + DailyChallengeScreen.ANIM_STAGE_GAP_MS;

      if ( day >= DAILY_CHALLENGE_DAYS_COUNT )
        continue;

      const arrow = arrows.get( day );
      if ( arrow )
        this.animateArrowBump( arrow, t );
      t += DailyChallengeScreen.ANIM_ARROW_MS + DailyChallengeScreen.ANIM_STAGE_GAP_MS;

      const next_refs = day_refs.get( day + 1 );
      if ( next_refs )
        this.transitionCardTo( next_refs, DayState.Current, t );
      t += DailyChallengeScreen.ANIM_POP_MS + DailyChallengeScreen.ANIM_STAGE_GAP_MS;
    }
  }

  private transitionCardTo( refs: DayCardRefs, to_state: DayState, start_time: number ): void
  {
    const bg_rect = refs.bg.getRect();
    const incoming: UIElement = refs.is_last
      ? new UINineSliceElement( this.ctx, 0, 0, bg_rect.getW(), bg_rect.getH(), imgIdForDayState( to_state ), "daily_challenge_last_day" )
      : new UIImage( this.ctx, 0, 0, bg_rect.getW(), bg_rect.getH(), imgIdForDayState( to_state ) );

    incoming.setClickable( false );
    incoming.setOpacityImmediate( 0 );

    refs.bg.addChildAtIndex( incoming, 0 );
    incoming.fadeIn( DailyChallengeScreen.ANIM_POP_MS, start_time, Easing.EaseOutQuad );

    const pop_half = DailyChallengeScreen.ANIM_POP_MS / 2;
    refs.bg.addTransform( new TransformScale(
      refs.bg, true, new Vector2( 1, 1 ), new Vector2( 1.08, 1.08 ),
      start_time, pop_half, { easing: Easing.EaseOutQuad }
    ) );
    refs.bg.addTransform( new TransformScale(
      refs.bg, true, new Vector2( 1.08, 1.08 ), new Vector2( 1, 1 ),
      start_time + pop_half, pop_half, { easing: Easing.EaseInOutQuad }
    ) );

    const revealIcon = ( el: UIElement, visible: boolean ): void =>
    {
      if ( !visible )
      {
        el.fadeOut( 140, start_time, Easing.EaseOutQuad );
        return;
      }

      el.setScaleImmediate( new Vector2( 0.5, 0.5 ) );
      el.fadeIn( 200, start_time + 60, Easing.EaseOutQuad );
      el.addTransform( new TransformScale(
        el, true, new Vector2( 0.5, 0.5 ), new Vector2( 1.15, 1.15 ),
        start_time + 60, 180, { easing: Easing.EaseOutQuad }
      ) );
      el.addTransform( new TransformScale(
        el, true, new Vector2( 1.15, 1.15 ), new Vector2( 1, 1 ),
        start_time + 240, 120, { easing: Easing.EaseInOutQuad }
      ) );
    };

    revealIcon( refs.play_btn, to_state === DayState.Current );
    revealIcon( refs.completed_icon, to_state === DayState.Completed );
    revealIcon( refs.locked_icon, to_state === DayState.Locked );
    refs.play_btn.setClickable( to_state === DayState.Current );

    for ( const icon of refs.reward_icons )
      icon.fadeToOpacity( to_state === DayState.Completed ? 0.5 : 1, 200, start_time );
  }

  private animateArrowBump( arrow: UIImage, start_time: number ): void
  {
    const bump_duration = Math.round( DailyChallengeScreen.ANIM_ARROW_MS * 0.45 );
    const settle_duration = DailyChallengeScreen.ANIM_ARROW_MS - bump_duration;

    arrow.addTransform( new TransformScale(
      arrow, true, new Vector2( 1, 1 ), new Vector2( 1.35, 1.35 ),
      start_time, bump_duration, { easing: Easing.EaseOutQuad }
    ) );
    arrow.addTransform( new TransformScale(
      arrow, true, new Vector2( 1.35, 1.35 ), new Vector2( 1, 1 ),
      start_time + bump_duration, settle_duration, { easing: Easing.EaseInOutQuad }
    ) );
  }

  // The stage's level lives on the server (today's pick of the stage pack);
  // the game screen must not be opened before the config is on hand, or it
  // would build an empty field.
  private async startStage( stage: number ): Promise<void>
  {
    if ( this.stage_loading )
      return;

    this.stage_loading = true;
    try
    {
      const loaded = await DailyChallengeService.loadLevel( stage );
      if ( !loaded )
      {
        if ( !NetworkManager.isOnline() )
          NetworkManager.showOfflineLevelsExhausted();
        else
          NetworkManager.handleLoadingDataError(
            new Error( `[DailyChallenge] level for stage ${stage} is not available` )
          );
        return;
      }
      DailyChallengePlayDialog.open( stage, PlayDialogState.Play );
    }
    finally
    {
      this.stage_loading = false;
    }
  }

  private static resetCountdownText(): string
  {
    const seconds = DailyChallengeService.getSecondsUntilReset();
    return seconds === null ? "" : formatShortDuration( seconds * 1000 );
  }

  private refreshTimerText(): void
  {
    if ( !this.timer_text ) return;

    const text = DailyChallengeScreen.resetCountdownText();
    if ( text === this.last_timer_text ) return;

    this.last_timer_text = text;
    this.timer_text.setText( text );
  }

  private initializeEventsController( canvas: HTMLCanvasElement ): void
  {
    if ( this.events_controller )
      this.events_controller.cleanup();

    this.events_controller = new BasicEventsController( canvas );
    this.events_controller.init(
      ( p: Vector2 ) => { super.onTouchStart( p, performance.now() ); },
      ( p: Vector2 ) => { super.onTouchEnd( p, performance.now() ); },
      ( p: Vector2 ) => { super.onTouchMove( p, performance.now() ); },
      true
    );
  }

  public destroy(): void
  {
    if ( this.events_controller )
    {
      this.events_controller.cleanup();
      this.events_controller = null;
    }
  }

  private getBackgroundGradient(): CanvasGradient
  {
    if ( !this.background_gradient )
    {
      const gradient = this.ctx.createLinearGradient( 0, 0, 0, Game.screenH() );
      gradient.addColorStop( 0, "#121A32" );
      gradient.addColorStop( 1, "#0244A8" );
      this.background_gradient = gradient;
    }

    return this.background_gradient;
  }

  // The fill must cover the whole canvas, not just the content rect: the
  // safe-area strips would otherwise stay transparent and expose whatever the
  // canvases below hold (the last WebGL frame of the previous level).
  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    if ( this.dom_mode )
    {
      this.setDomBackdrop( "#04409C" );
      this.startRender();
      this.finishRender();
      SafeAreas.syncBothSafeAreaOverlaysDom( this.getDomHost() );
      return;
    }

    this.ctx.fillStyle = "#04409C";
    this.ctx.fillRect( 0, 0, Game.screenW(), Game.screenH() );

    this.startRender();
    this.finishRender();
    SafeAreas.renderBothSafeAreaOverlays( this.ctx );
  }

  public update( now: number ): void
  {
    if ( now - this.timer_refreshed_at > 1000 )
    {
      this.timer_refreshed_at = now;
      this.refreshTimerText();
    }

    if ( DailyChallengeService.isPeriodOver()
      && !this.stage_loading
      && now - this.rollover_attempt_at > 15_000 )
    {
      this.rollover_attempt_at = now;
      void this.reloadAfterReset();
    }

    this.updateBasic( now );
  }

  private async reloadAfterReset(): Promise<void>
  {
    await DailyChallengeService.fetch();
    if ( DailyChallengeService.isPeriodOver() || this.stage_loading )
      return;
    if ( ScreenManager.getCurrentScreen() !== Screens.DAILY_CHALLENGE )
      return;
    await ScreenManager.showScreen( Screens.DAILY_CHALLENGE );
  }

  public name(): string
  {
    return "DailyChallengeScreen";
  }
}
